/**
 * Core: Query
 * -----------------------------------------------------------------------------
 * GraphQL query tool, acts as a replacement for Apollo.
 * - Do not query admin API from JS as you will need to publicly store the API
 *   access token and password which is a security risk.
 *
 */

import { augmentB2bPrice, augmentB2bPrices } from '~/helpers/product'

/**
 * Set global variables.
 */
const queryCacheKey = 'cnvs-query-cache'

/**
 * Query storefront API.
 * @param {Object} data - Parameters.
 * @param {Object} data.query - GraphQL query.
 * @param {Object} data.variables - GraphQL variables.
 * @param {Boolean} useCache - Use sessionStorage cache.
 * @returns {Promise}
 */
export default ({ query: graphqlQuery, variables }, useCache = true) => {
  return new Promise(async(resolve, reject) => {
    const { store, token, version } = cnvs.credentials.storefront
    const url = `https://${store}.myshopify.com/api/${version}/graphql.json`

    /**
     * Set headers.
     */
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token,
    }

    /**
     * Add language header.
     * - Storefront API does not support kebab-case language code so this is
     *   converted into SCREAMING_SNAKE_CASE.
     * - E.g. ZH-CN doesn't work, but ZH_CH does.
     * - Does not convert `Accept-Language` header as this expects kebab-case.
     */
    if (variables?.language) {
      headers['Accept-Language'] = variables.language
      const formattedLanguage = variables.language.toUpperCase().replaceAll('-', '_')
      variables.language = formattedLanguage
    }

    /**
     * Build storage key and check if query has been cached.
     */
    const storageKey = buildStorageKey(graphqlQuery, variables)
    const queryCache = JSON.parse(sessionStorage.getItem(queryCacheKey))

    if (
      useCache &&
      !cnvs.settings.disableQueryCache &&
      !cnvs.storybook &&
      queryCache &&
      queryCache[storageKey]
    ) {
      resolve(queryCache[storageKey])
      return
    }

    /**
     * Fetch response.
     */
    let response = {}

    try {
      response = await fetch(url, {
        body: JSON.stringify({
          query: buildQuery(graphqlQuery),
          variables,
        }),
        headers,
        method: 'POST',
      })

      if (!response.ok) {
        reject(response.description)
        return
      }

      response = await response.json()

    } catch (error) {
      reject(error)
      return
    }

    /**
     * Augment product data with b2b pricebooks
     * B2b pricebooks are retrieved from querying `product.price.liquid`
     */
    if (window.cnvs.customer.b2b && response?.data) {
      const metaobject = response.data.metaobject

      if (response.data.product) {
        // product.gql
        await augmentB2bPrice(response.data.product)
      } else if (graphqlQuery.products && response.data.nodes) {
        // products.gql
        await augmentB2bPrices(response.data.nodes)
      } else if (metaobject) {
        // metaobject.gql
        const productList = metaobject.fields.find(field => field.type === 'list.product_reference')
        if (productList?.references?.nodes) {
          await augmentB2bPrices(productList.references.nodes)
        }
      }
    }

    /**
     * Set response to cache.
     */
    if (useCache) {
      try {
        const data = JSON.parse(sessionStorage.getItem(queryCacheKey)) || {}
        data[storageKey] = response.data
        sessionStorage.setItem(queryCacheKey, JSON.stringify(data))

      } catch (error) {
        reportError(error)
      }
    }

    resolve(response.data)
  })
}

/**
 * Build GraphQL query string from AST format data.
 * @param {Object} astData - AST format GraphQL query.
 * @returns {String}
 */
function buildQuery(astData) {

  /**
   * Uses body source as starting point.
   * - Remove import tags.
   */
  let query = astData.loc.source.body
    .replace(/#import.*\n/g, '')
    .trim()

  const fragments = {}

  /**
   * Go through each fragment definition and build query string.
   */
  astData.definitions.forEach((definition) => {
    if (definition.kind !== 'FragmentDefinition') {
      return
    }

    const fields = buildFields(definition.selectionSet.selections)
    fragments[definition.name.value] = fields.join('\n')
  })

  /**
   * Replace fragment object with query.
   */
  Object.entries(fragments).forEach(([key, value]) => {
    let formattedValue = value

    /**
     * Resolves nested fragments.
     */
    Object.entries(fragments).forEach(([fragmentKey, fragmentValue]) => {
      formattedValue = formattedValue.replace(`...${fragmentKey}`, fragmentValue)
    })

    query = query.replace(`...${key}`, formattedValue)
  })

  return query
}

/**
 * Build fields from selection sets.
 * - Supports arguments and nested fields.
 * @param {Array} selections - Selections array of objects.
 * @returns {String}
 */
function buildFields(selections) {
  return selections.map((selection) => {
    let field = selection.alias
      ? `${selection.alias?.value}: ${selection.name?.value}`
      : selection.name?.value

    /**
     * Inline fragments.
     */
    if (selection.kind === 'InlineFragment') {
      field = `... on ${selection.typeCondition.name.value}`
    }

    /**
     * Preserves fragment syntax.
     */
    if (selection.kind === 'FragmentSpread') {
      field = `...${selection.name.value}`
    }

    /**
     * If field has argument append to variable.
     */
    if (selection.arguments?.length) {
      const args = selection.arguments.map((argument) => {
        let value = argument.value.value

        switch (argument.value.kind) {
          case 'ListValue':
            value = buildListArgumentValue(argument)
            break

          case 'StringValue':
            value = `"${argument.value.value}"`
            break

          case 'Variable':
            value = `$${argument.value.name.value}`
            break
        }

        return `${argument.name.value}: ${value}`
      })

      field += `(${args.join(', ')})`
    }

    /**
     * If field has nested fields then append to variable.
     */
    if (selection.selectionSet) {
      const fields = buildFields(selection.selectionSet.selections)
      field += ` {\n${fields.join('\n')}\n}`
    }

    return field
  })
}

/**
 * Builds value of argument when it's a list value (array of objects).
 * @param {Object} argument - AST format argument object.
 * @returns {String}
 */
function buildListArgumentValue(argument) {
  const values = argument.value.values.map((value) => {
    const fields = value.fields.map((field) => {
      return `${field.name.value}: "${field.value.value}"`
    })

    return `{${fields.join(', ')}}`
  })

  return `[${values.join(', ')}]`
}

/**
 * Build storage key for sessionStorage caching.
 * @param {Object} astData - AST format GraphQL query.
 * @param {Object} variables - GraphQL variables.
 * @returns {String}
 */
function buildStorageKey(astData, variables = {}) {
  const queryString = astData.definitions
    .map((definition) => {
      const type = definition.operation || 'fragment'
      return `${type}:"${definition.name.value}"`
    })
    .join('-')

  const variableString = Object.entries(variables)
    .map(([key, value]) => {
      return `${key}:${JSON.stringify(value)}`
    })
    .join('-')

  return `${queryString}-length:${astData.loc.end}-${variableString}`
}
